import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { sessionFeedbackApi } from "@/apis/sessionFeedback";

interface SessionFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  sessionId: number;
  sessionTitle?: string;
  onFeedbackUpdated?: (feedback: string | null) => void;
}

/**
 * 세션 피드백 모달 컴포넌트
 *
 * @description 세션에 대한 관리자 피드백을 생성/수정/삭제할 수 있는 모달
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export default function SessionFeedbackModal({
  open,
  onClose,
  sessionId,
  sessionTitle,
  onFeedbackUpdated,
}: SessionFeedbackModalProps) {
  const [feedback, setFeedback] = useState<string>("");
  const [originalFeedback, setOriginalFeedback] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  // 모달이 열릴 때 기존 피드백 조회
  useEffect(() => {
    if (open && sessionId) {
      loadFeedback();
    }
  }, [open, sessionId]);

  // 모달이 닫힐 때 상태 초기화
  useEffect(() => {
    if (!open) {
      setFeedback("");
      setOriginalFeedback(null);
      setError(null);
    }
  }, [open]);

  /**
   * 기존 피드백을 로드합니다.
   */
  const loadFeedback = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log("[FEEDBACK_MODAL]", {
        action: "loadFeedback",
        sessionId,
      });

      const existingFeedback = await sessionFeedbackApi.getFeedback(sessionId);

      setOriginalFeedback(existingFeedback);
      setFeedback(existingFeedback || "");

      console.log("[FEEDBACK_MODAL]", {
        action: "loadFeedback",
        result: "success",
        hasFeedback: !!existingFeedback,
      });
    } catch (error: any) {
      console.error("[FEEDBACK_MODAL_ERROR]", {
        action: "loadFeedback",
        error: error.message,
      });

      let errorMessage = "Failed to load feedback. Please try again.";

      if (error.response?.status === 404) {
        errorMessage = "Session not found.";
      } else if (error.response?.status === 401) {
        errorMessage = "Authentication failed. Please log in again.";
      }

      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  /**
   * 피드백을 저장합니다.
   */
  const handleSave = async () => {
    if (!feedback.trim()) {
      enqueueSnackbar("Please enter feedback content.", { variant: "warning" });
      return;
    }

    setSaving(true);
    setError(null);

    try {
      console.log("[FEEDBACK_MODAL]", {
        action: "saveFeedback",
        sessionId,
        isUpdate: !!originalFeedback,
        feedbackLength: feedback.length,
      });

      let savedFeedback: string;

      if (originalFeedback) {
        // 수정
        savedFeedback = await sessionFeedbackApi.updateFeedback(
          sessionId,
          feedback.trim()
        );
        enqueueSnackbar("Feedback updated successfully.", {
          variant: "success",
        });
      } else {
        // 생성
        savedFeedback = await sessionFeedbackApi.createFeedback(
          sessionId,
          feedback.trim()
        );
        enqueueSnackbar("Feedback created successfully.", {
          variant: "success",
        });
      }

      setOriginalFeedback(savedFeedback);
      onFeedbackUpdated?.(savedFeedback);
      onClose();

      console.log("[FEEDBACK_MODAL]", {
        action: "saveFeedback",
        result: "success",
        sessionId,
      });
    } catch (error: any) {
      console.error("[FEEDBACK_MODAL_ERROR]", {
        action: "saveFeedback",
        error: error.message,
      });

      let errorMessage = "Failed to save feedback. Please try again.";

      if (error.response?.status === 404) {
        errorMessage = "Session not found.";
      } else if (error.response?.status === 401) {
        errorMessage = "Authentication failed. Please log in again.";
      } else if (error.response?.status === 400) {
        errorMessage = "Invalid feedback content.";
      }

      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  /**
   * 피드백을 삭제합니다.
   */
  const handleDelete = async () => {
    if (!originalFeedback) {
      enqueueSnackbar("No feedback to delete.", { variant: "warning" });
      return;
    }

    if (!window.confirm("Are you sure you want to delete this feedback?")) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      console.log("[FEEDBACK_MODAL]", {
        action: "deleteFeedback",
        sessionId,
      });

      await sessionFeedbackApi.deleteFeedback(sessionId);

      setFeedback("");
      setOriginalFeedback(null);
      onFeedbackUpdated?.(null);
      onClose();

      enqueueSnackbar("Feedback deleted successfully.", { variant: "success" });

      console.log("[FEEDBACK_MODAL]", {
        action: "deleteFeedback",
        result: "success",
        sessionId,
      });
    } catch (error: any) {
      console.error("[FEEDBACK_MODAL_ERROR]", {
        action: "deleteFeedback",
        error: error.message,
      });

      let errorMessage = "Failed to delete feedback. Please try again.";

      if (error.response?.status === 404) {
        errorMessage = "Session not found.";
      } else if (error.response?.status === 401) {
        errorMessage = "Authentication failed. Please log in again.";
      }

      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  /**
   * 변경사항이 있는지 확인합니다.
   */
  const hasChanges = feedback.trim() !== (originalFeedback || "");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: 800,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Session Feedback
          </Typography>
          {sessionTitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {sessionTitle}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Session ID: {sessionId}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              multiline
              rows={30}
              label="Admin Feedback"
              placeholder="Enter your feedback about this chat session..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              disabled={saving || deleting}
              inputProps={{
                maxLength: 3000,
              }}
              helperText={`${feedback.length}/3000 characters`}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            {originalFeedback && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Last updated: {new Date().toLocaleDateString()}
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={saving || deleting}
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>

        {originalFeedback && (
          <Button
            onClick={handleDelete}
            disabled={saving || deleting || loading}
            startIcon={
              deleting ? <CircularProgress size={16} /> : <DeleteIcon />
            }
            color="error"
            sx={{ borderRadius: 2 }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        )}

        <Button
          onClick={handleSave}
          disabled={
            !hasChanges || saving || deleting || loading || !feedback.trim()
          }
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          variant="contained"
          sx={{ borderRadius: 2 }}
        >
          {saving ? "Saving..." : originalFeedback ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
